import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { LoadStatusBadge } from '../components/Badges';
import {
  ChevronLeft, MapPin, Navigation, Clock, Truck, Play, Pause,
  RefreshCw, Share2, Copy, CheckCircle, Loader2, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom truck icon
const truckIcon = new L.DivIcon({
  className: 'custom-truck-icon',
  html: `<div style="background: linear-gradient(135deg, #4ADE80 0%, #16A34A 100%); width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 10px rgba(0,0,0,0.3);">
    <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
      <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
    </svg>
  </div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 18],
});

// Pickup marker
const pickupIcon = new L.DivIcon({
  className: 'custom-pickup-icon',
  html: `<div style="background: #22c55e; width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 2px solid white;">
    <span style="color: white; font-weight: bold; font-size: 12px;">P</span>
  </div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

// Delivery marker
const deliveryIcon = new L.DivIcon({
  className: 'custom-delivery-icon',
  html: `<div style="background: #ef4444; width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 2px solid white;">
    <span style="color: white; font-weight: bold; font-size: 12px;">D</span>
  </div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

// Map component that auto-centers
const MapController = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, zoom || map.getZoom());
    }
  }, [center, zoom, map]);
  return null;
};

const LoadTrackingPage = () => {
  const { loadId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [trackingInfo, setTrackingInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isTracking, setIsTracking] = useState(false);
  const [watchId, setWatchId] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [copied, setCopied] = useState(false);

  const isDriver = trackingInfo?.driver_name && user;

  const loadTrackingInfo = useCallback(async () => {
    try {
      const response = await api.get(`/tracking/${loadId}/info`);
      setTrackingInfo(response.data);
    } catch (error) {
      toast.error('Failed to load tracking info');
    } finally {
      setLoading(false);
    }
  }, [loadId]);

  useEffect(() => {
    loadTrackingInfo();
    
    // Auto-refresh every 10 seconds if enabled
    let interval;
    if (autoRefresh) {
      interval = setInterval(loadTrackingInfo, 10000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
      if (watchId) navigator.geolocation.clearWatch(watchId);
    };
  }, [loadTrackingInfo, autoRefresh, watchId]);

  const startTracking = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    setIsTracking(true);
    toast.success('Location tracking started');

    const id = navigator.geolocation.watchPosition(
      async (position) => {
        const { latitude, longitude, accuracy, speed, heading, altitude } = position.coords;
        
        try {
          await api.post(`/tracking/${loadId}/location`, {
            latitude,
            longitude,
            accuracy,
            speed: speed ? speed * 3.6 : null, // Convert m/s to km/h
            heading,
            altitude
          });
          setLastUpdate(new Date());
          loadTrackingInfo();
        } catch (error) {
          console.error('Failed to update location');
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        toast.error('Failed to get location');
      },
      {
        enableHighAccuracy: true,
        maximumAge: 10000,
        timeout: 5000
      }
    );

    setWatchId(id);
  };

  const stopTracking = async () => {
    if (watchId) {
      navigator.geolocation.clearWatch(watchId);
      setWatchId(null);
    }
    
    try {
      await api.post(`/tracking/${loadId}/stop`);
      toast.success('Tracking stopped');
    } catch (error) {
      toast.error('Failed to stop tracking');
    }
    
    setIsTracking(false);
  };

  const copyTrackingLink = () => {
    const link = `${window.location.origin}/track/${loadId}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success('Tracking link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#4ADE80]" />
      </div>
    );
  }

  // Calculate map center and route
  const currentLoc = trackingInfo?.current_location;
  const routePoints = trackingInfo?.location_history?.map(loc => [loc.latitude, loc.longitude]) || [];
  
  // Default center (US center) if no location data
  const mapCenter = currentLoc 
    ? [currentLoc.latitude, currentLoc.longitude]
    : [39.8283, -98.5795];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 bg-[#12151a] border-b border-[#1a1d24] z-[1000] px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-white flex items-center gap-2">
              <Navigation className="w-5 h-5 text-[#4ADE80]" />
              Live Tracking
            </h1>
            <p className="text-xs text-gray-400">
              {trackingInfo?.pickup_location} → {trackingInfo?.delivery_location}
            </p>
          </div>
          <LoadStatusBadge status={trackingInfo?.status} />
        </div>
      </div>

      {/* Map */}
      <div className="pt-16 h-[60vh]">
        <MapContainer
          center={mapCenter}
          zoom={currentLoc ? 12 : 4}
          style={{ height: '100%', width: '100%' }}
          zoomControl={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          <MapController center={mapCenter} zoom={currentLoc ? 12 : 4} />
          
          {/* Route polyline */}
          {routePoints.length > 1 && (
            <Polyline
              positions={routePoints}
              color="#4ADE80"
              weight={4}
              opacity={0.8}
            />
          )}
          
          {/* Current location marker */}
          {currentLoc && (
            <Marker position={[currentLoc.latitude, currentLoc.longitude]} icon={truckIcon}>
              <Popup>
                <div className="text-center">
                  <strong>Current Location</strong>
                  <br />
                  {currentLoc.speed && (
                    <span>{Math.round(currentLoc.speed)} km/h</span>
                  )}
                  <br />
                  <span className="text-xs text-gray-500">
                    {new Date(currentLoc.created_at).toLocaleTimeString()}
                  </span>
                </div>
              </Popup>
            </Marker>
          )}
        </MapContainer>
      </div>

      {/* Info Panel */}
      <div className="bg-[#0a0c10] px-4 py-4 space-y-4">
        {/* Status Cards */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="bg-[#12151a] border-[#1a1d24]">
            <CardContent className="p-3 text-center">
              <Truck className="w-5 h-5 mx-auto text-[#4ADE80] mb-1" />
              <p className="text-xs text-gray-400">Driver</p>
              <p className="text-sm text-white font-medium truncate">
                {trackingInfo?.driver_name || 'N/A'}
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-[#12151a] border-[#1a1d24]">
            <CardContent className="p-3 text-center">
              <Clock className="w-5 h-5 mx-auto text-blue-400 mb-1" />
              <p className="text-xs text-gray-400">Last Update</p>
              <p className="text-sm text-white font-medium">
                {currentLoc 
                  ? new Date(currentLoc.created_at).toLocaleTimeString()
                  : 'No data'
                }
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-[#12151a] border-[#1a1d24]">
            <CardContent className="p-3 text-center">
              <MapPin className="w-5 h-5 mx-auto text-green-400 mb-1" />
              <p className="text-xs text-gray-400">Points</p>
              <p className="text-sm text-white font-medium">
                {trackingInfo?.location_history?.length || 0}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Current Speed & Location */}
        {currentLoc && (
          <Card className="bg-[#12151a] border-[#1a1d24]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-400">Current Speed</p>
                  <p className="text-2xl font-bold text-[#4ADE80]">
                    {currentLoc.speed ? `${Math.round(currentLoc.speed)} km/h` : '0 km/h'}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-400">Coordinates</p>
                  <p className="text-sm text-white font-mono">
                    {currentLoc.latitude.toFixed(4)}, {currentLoc.longitude.toFixed(4)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* No Tracking Data */}
        {!currentLoc && trackingInfo?.status === 'picked_up' && (
          <Card className="bg-[#12151a] border-[#1a1d24]">
            <CardContent className="p-6 text-center">
              <AlertCircle className="w-10 h-10 text-yellow-500 mx-auto mb-3" />
              <p className="text-white font-medium">No Location Data</p>
              <p className="text-sm text-gray-400 mt-1">
                The driver hasn't started sharing their location yet.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Driver Controls */}
        <div className="flex gap-3">
          {trackingInfo?.status === 'picked_up' && (
            <>
              {!isTracking ? (
                <Button
                  onClick={startTracking}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Start Sharing Location
                </Button>
              ) : (
                <Button
                  onClick={stopTracking}
                  variant="destructive"
                  className="flex-1"
                >
                  <Pause className="w-4 h-4 mr-2" />
                  Stop Sharing
                </Button>
              )}
            </>
          )}
          
          <Button
            variant="outline"
            onClick={loadTrackingInfo}
            className="px-4"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          
          <Button
            variant="outline"
            onClick={copyTrackingLink}
            className="px-4"
          >
            {copied ? <CheckCircle className="w-4 h-4 text-green-400" /> : <Share2 className="w-4 h-4" />}
          </Button>
        </div>

        {/* Auto-refresh toggle */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-400">Auto-refresh (10s)</span>
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`w-10 h-6 rounded-full transition-colors ${
              autoRefresh ? 'bg-[#4ADE80]' : 'bg-gray-700'
            }`}
          >
            <div className={`w-4 h-4 rounded-full bg-white transform transition-transform ${
              autoRefresh ? 'translate-x-5' : 'translate-x-1'
            }`} />
          </button>
        </div>

        {/* Last tracking update */}
        {lastUpdate && (
          <p className="text-xs text-center text-gray-500">
            Your location last sent: {lastUpdate.toLocaleTimeString()}
          </p>
        )}
      </div>
    </div>
  );
};

export default LoadTrackingPage;
